import { ReactNode } from 'react';

import { Alert, AlertColor } from '@mui/material';
import { useSnackbar, VariantType, OptionsObject } from 'notistack';

import { IconButton } from '@/components/ui/IconButton';
import { uiIcons } from '@/constants/uiIcons';

export interface NotificationOptions extends Omit<OptionsObject, 'variant'> {
  /**
   * Message to display in the notification
   */
  message: string | ReactNode;

  /**
   * Optional action button(s) for the notification
   * Actions are automatically keyboard accessible (Tab + Enter)
   */
  action?: ReactNode;

  /**
   * Whether the notification should persist until manually closed
   * Useful for notifications with actions that require user interaction
   * Default: false
   */
  persist?: boolean;

  /**
   * Custom auto-hide duration in milliseconds
   * If not specified, uses default durations
   */
  autoHideDuration?: number;
}

/**
 * Custom hook for displaying accessible notifications using Notistack
 *
 * @example
 * ```tsx
 * const { showSuccess, showError, showInfo, showWarning } = useNotification();
 *
 * Simple success notification (auto-hides in 3s)
 * showSuccess({ message: 'Item saved successfully' });
 *
 * Error notification (auto-hides in 7s)
 * showError({ message: 'Failed to save item' });
 *
 * Notification with action (auto-hides in 6s)
 * showInfo({
 *   message: 'Item deleted',
 *   action: <Button onClick={handleUndo}>Undo</Button>,
 *   autoHideDuration: 6000,
 * });
 *
 * Persistent notification (must be manually closed)
 * showWarning({
 *   message: 'Network connection lost',
 *   persist: true,
 * });
 * ```
 */
export const useNotification = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const showNotification = (
    variant: VariantType,
    options: NotificationOptions,
  ) => {
    const { message, action, persist, autoHideDuration, ...rest } = options;

    // Determine auto-hide duration based on context
    let duration = autoHideDuration;
    if (!duration && !persist) {
      if (action) {
        // Notifications with actions get 6s
        duration = 6000;
      } else if (variant === 'error' || variant === 'warning') {
        // Errors/warnings get 7s for readability
        duration = 7000;
      } else {
        // Success/info get 3s minimum for accessibility and readability
        duration = 3000;
      }
    }

    // Show close button for errors/warnings or notifications with actions/persist
    const needsCloseButton =
      variant === 'error' || variant === 'warning' || !!action || persist;

    // error/warning use role="alert", success/info use role="status"
    return enqueueSnackbar(message, {
      // Don't pass variant to Notistack since Alert handles styling
      persist,
      autoHideDuration: persist ? undefined : duration,
      // Make Notistack's container transparent so only MUI Alert shows
      style: {
        background: 'transparent',
        boxShadow: 'none',
        padding: 0,
      },
      // Use Notistack's content callback to get access to the snackbar key
      content: (key) => (
        <Alert
          severity={variant as AlertColor}
          action={
            needsCloseButton ? (
              <>
                {action}
                <IconButton
                  size="small"
                  tooltip="Close notification"
                  color="inherit"
                  onClick={() => closeSnackbar(key)}
                  iconName={uiIcons.actions.close}
                />
              </>
            ) : (
              action
            )
          }
        >
          {message}
        </Alert>
      ),
      ...rest,
    });
  };

  return {
    showSuccess: (options: NotificationOptions) =>
      showNotification('success', options),

    showError: (options: NotificationOptions) =>
      showNotification('error', options),

    showInfo: (options: NotificationOptions) =>
      showNotification('info', options),

    showWarning: (options: NotificationOptions) =>
      showNotification('warning', options),

    /**
     * Manually close a specific notification by its key
     * @param key - The key returned from show* methods
     */
    closeNotification: closeSnackbar,

    /**
     * Close all notifications at once
     */
    closeAllNotifications: () => closeSnackbar(),
  };
};
